// Übersetzung der API-Antwort in Fachsprache.
//
// WARUM DIESE DATEI GETRENNT IST: NFR-UX-003 verbietet Technikbegriffe auf der
// Kernoberfläche ausdrücklich -- "Party", "Entity Resolution", "Query-AST" sind
// dort namentlich genannt. Für diesen Bildschirm heißt das: kein
// "operatingContextId", kein "reasonCode", kein "groupsClaimStatus" im
// sichtbaren Text. Ein Grundcode ist eine stabile Kennung für Log und Audit und
// war nie für Menschen gedacht.
//
// Die Übersetzung liegt bewusst NICHT in der Komponente. Sonst entstünde sie
// bei jedem neuen Bildschirm erneut, und ein Grundcode ohne Satz würde
// irgendwann einfach durchgereicht -- genau das, was die Anforderung verhindern
// soll. Ein fehlender Fall ist hier eine Lücke im Typsystem, keine hässliche
// Zeichenkette auf dem Bildschirm.

import { anzahlWort, namenListe, type HausArt } from './haus-art.ts';

/** Was die Anwendung dem Menschen sagt, und was er tun kann. */
export interface PlainMessage {
  readonly headline: string;
  readonly explanation: string;
  /**
   * Was der Mensch jetzt tun kann. NFR-UX-005 verlangt Fehlererholung: ein
   * Fehlertext ohne nächsten Schritt lässt Menschen ratlos zurück.
   */
  readonly nextStep: string;
  /** Ob ein erneuter Versuch überhaupt etwas ändern kann. */
  readonly retryable: boolean;
  /**
   * Ein Hinweis, kein Fehler: der Stand ist schon so, wie die Nutzerin ihn
   * wollte (etwa "bereits beendet"). Die Leiste ist dann informativ statt
   * warnend -- ein Warnton behauptete ein Problem, das es nicht gibt.
   */
  readonly notice?: true;
  /**
   * Die Vorgangsnummer der Antwort -- gesetzt genau dann, wenn ein Satz auf sie
   * verweist ("Nenne ihr die Vorgangsnummer unten."). Der Bildschirm zeigt sie
   * dann unter den Saetzen; ohne sie stuende der Verweis ins Leere.
   */
  readonly correlationId?: string;
}

const MESSAGES: Readonly<Record<string, PlainMessage>> = Object.freeze({
  subject_access_blocked: {
    headline: 'Dein Zugang ist gesperrt.',
    explanation:
      'Die LIAISON-Administration hat deinen Zugang vorübergehend gesperrt. Das ist keine Störung und kein Fehler deiner Anmeldung.',
    nextStep: 'Wende dich an die LIAISON-Administration. Sie kann dir sagen, warum und wie lange.',
    retryable: false,
  },
  context_groupwide_requires_entitlement: {
    headline: 'Du bist noch für keinen Bereich freigeschaltet.',
    explanation:
      'Deine Anmeldung hat funktioniert, aber dir ist noch kein Verlag zugeordnet. Ohne Zuordnung gibt es nichts, was LIAISON dir zeigen könnte.',
    nextStep: 'Bitte deine Administration, dich einem Verlag zuzuordnen.',
    retryable: false,
  },
  no_matching_rule: {
    headline: 'Für diese Ansicht fehlt dir die Berechtigung.',
    explanation: 'Du bist angemeldet, aber deine Rolle erlaubt diese Ansicht nicht.',
    nextStep: 'Wenn du sie brauchst, bitte deine Administration um die passende Rolle.',
    retryable: false,
  },
  entitlement_groups_overage: {
    headline: 'Deine Anmeldung trägt zu viele Gruppen.',
    explanation:
      'Dein Konto ist in so vielen Gruppen, dass die Anmeldung sie nicht mehr alle übermitteln kann. LIAISON kann deine Zuordnung deshalb nicht bestimmen.',
    nextStep: 'Das lässt sich nur von der IT lösen. Ein erneuter Versuch ändert nichts.',
    retryable: false,
  },
  entitlement_groups_claim_invalid: {
    headline: 'Deine Anmeldung ist falsch eingerichtet.',
    explanation: 'Die Anmeldung liefert deine Gruppen in einer Form, die LIAISON nicht verwerten kann.',
    nextStep: 'Das ist ein Einrichtungsfehler und gehört zur IT. Ein erneuter Versuch ändert nichts.',
    retryable: false,
  },
  entitlement_lookup_unavailable: {
    headline: 'LIAISON ist gerade nicht erreichbar.',
    explanation:
      'Deine Anmeldung ist in Ordnung. LIAISON kann im Moment nur nicht nachsehen, für welche Bereiche du freigeschaltet bist.',
    nextStep: 'Versuche es in einer Minute noch einmal.',
    retryable: true,
  },
  token_missing: {
    headline: 'Du bist nicht angemeldet.',
    explanation: 'Für diese Ansicht braucht LIAISON deine Anmeldung.',
    nextStep: 'Melde dich mit deinem Unternehmenskonto an.',
    retryable: true,
  },
  token_too_long: {
    headline: 'Deine Anmeldung ist zu umfangreich.',
    explanation:
      'Die Anmeldung deines Kontos ist größer als LIAISON verarbeiten kann. Das passiert, wenn ein Konto in sehr vielen Gruppen ist.',
    nextStep: 'Das lässt sich nur von der IT lösen. Ein erneuter Versuch ändert nichts.',
    retryable: false,
  },
  audit_unavailable: {
    // Der Grundcode entsteht an zwei Stellen: wenn eine Ablehnung nicht
    // protokolliert werden kann (ADR-0026 Punkt 4) und wenn der Nachweis einer
    // Anlage scheitert (ADR-0015 Punkt 9). Beide Male ist die Aussage
    // dieselbe, und der Satz sagt sie ausdrücklich: es ist nichts halb
    // passiert. Ohne diesen Halbsatz bliebe die Nutzerin im Zweifel, ob sie
    // den Vorgang gerade doppelt anlegt.
    headline: 'LIAISON konnte den Vorgang nicht abschließen.',
    explanation:
      'LIAISON schreibt jeden Vorgang zusammen mit seinem Nachweis. Der Nachweis ließ sich gerade nicht schreiben, deshalb wurde auch der Vorgang selbst nicht gespeichert — nichts ist halb passiert.',
    nextStep: 'Versuche es in einer Minute noch einmal. Deine Eingaben bleiben erhalten.',
    retryable: true,
  },
  request_body_too_large: {
    // Der einzige der sechs Rahmungsfehler, den eine Nutzerin selbst auslöst
    // und selbst beheben kann: ein eingefügter langer Textabschnitt reißt die
    // Größengrenze des Vorgangs. Deshalb nennt der nächste Schritt eine
    // Handlung — kürzen — und KEINEN erneuten Versuch. "Versuche es erneut"
    // wäre hier der falsche Rat: derselbe Inhalt scheitert wieder.
    headline: 'Deine Eingabe ist zu umfangreich.',
    explanation:
      'LIAISON nimmt je Vorgang eine begrenzte Menge Text an. Deine Eingabe liegt darüber, deshalb wurde nichts gespeichert.',
    nextStep:
      'Kürze den längsten Eintrag — meist ist es ein eingefügter Textabschnitt — und speichere dann erneut.',
    retryable: false,
  },
  token_expired: {
    headline: 'Deine Anmeldung ist abgelaufen.',
    explanation: 'Anmeldungen gelten nur eine begrenzte Zeit. Das ist Absicht und kein Fehler.',
    nextStep: 'Melde dich einfach neu an.',
    retryable: true,
  },
  // Kolibri S1b (DESIGN K5): der Server lehnt jede Aenderung an einem
  // Kolibri-Feld ab. Telefon und E-Mail stehen seit Michaels Entscheidung vom
  // 20.10.2026 mit in der Liste; die Kundennummer ist eine Kolibri-Kennung.
  field_owned_by_source: {
    headline: 'Diese Angabe kommt aus Kolibri.',
    explanation:
      'Firmenname, Rechtsform, Kolibri-Anschrift, Kundennummer, USt-IdNr., GLN, Telefon und E-Mail pflegt Kolibri. Es wurde nichts geändert.',
    nextStep: 'Melde die Änderung bei Kolibri. Ergänzen kannst du hier weiterhin, etwa eine weitere Anschrift.',
    retryable: false,
  },
});

/**
 * Was die Antwort ueber sich selbst hergibt -- die Grundlage jedes Rueckfalls.
 *
 * WARUM DIESE DREI ANGABEN UND NICHT MEHR: der Rueckfalltext hat bis zum
 * 31.08.2026 nichts von der Antwort gewusst. Er war EIN Satz mit `retryable:
 * true`, und beides war falsch, sobald er tatsaechlich auftrat. An
 * `https://dev.liaison.bonnier.de` beantwortete der statische Host
 * `/platform/access-decision` mit seiner eigenen 404-Seite in HTML
 * (`public/staticwebapp.config.json` nimmt `/platform/*` vom Rueckschreiben auf
 * die Anwendungsseite aus). Die Oberflaeche sah eine Ablehnung ohne lesbare
 * Auskunft, riet zum erneuten Versuch -- der nie etwas aendern konnte --, verwies
 * auf ein Betriebsprotokoll, das die Nutzerin nicht hat, und bat um eine
 * Vorgangsnummer, die zwei Zeilen tiefer als "nicht uebermittelt" stand.
 *
 * Der Katalog oben haelt an jedem seiner Grundcodes einen Standard, den der
 * Rueckfall verfehlte: `retryable` sagt die Wahrheit, der naechste Schritt nennt
 * eine HANDLUNG (NFR-UX-005), und kein Satz verweist auf etwas, das die
 * Nutzerin nicht in der Hand hat.
 */
export interface ResponseFacts {
  /**
   * Der Status der Antwort, `0` wenn gar keine zustande kam. Er allein
   * entscheidet ueber die Wiederholbarkeit.
   */
  readonly status: number;
  /**
   * Ob ueberhaupt eine lesbare Auskunft von LIAISON ankam.
   *
   * Nicht "welcher Dienst hat geantwortet" und ausdruecklich KEINE Auswertung
   * des Medientyps: gepruefte Eigenschaft ist, ob sich die Antwort als
   * Auskunft von LIAISON lesen laesst. Das trifft die 404-Seite des Hosts von
   * heute, und es trifft genauso einen Zwischenspeicher, ein Gateway oder eine
   * Wartungsseite von morgen. Eine Bedingung auf `text/html` haette nur den
   * heutigen Fall getroffen.
   */
  readonly answered: boolean;
  /** Ob eine Vorgangsnummer vorliegt. Nur dann darf ein Satz sie nennen. */
  readonly hasCorrelationId: boolean;
  /** Die Nummer selbst, damit der Bildschirm sie zeigen kann, auf die der Satz verweist. */
  readonly correlationId?: string | undefined;
}

/** Vorgangsnummer aus der Fehlerantwort von LIAISON; nur ein Text zaehlt als Nummer. */
export function correlationOf(body: Readonly<Record<string, unknown>>): Pick<ResponseFacts, 'hasCorrelationId' | 'correlationId'> {
  const id = body['correlationId'];
  return typeof id === 'string' && id.trim() !== '' ? { hasCorrelationId: true, correlationId: id } : { hasCorrelationId: false };
}

/**
 * Haengt den Hinweis auf die Vorgangsnummer an -- und nur dann, wenn es eine gibt.
 *
 * Der Halbsatz stand vorher fest im Rueckfalltext. Genau in dem Fall, in dem
 * der Rueckfall auftrat, gab es keine Nummer: die Meldung bat um eine Angabe,
 * die der Bildschirm selbst als fehlend auswies.
 */
function withCorrelation(text: string, hasCorrelationId: boolean): string {
  return hasCorrelationId ? `${text} Nenne ihr die Vorgangsnummer unten.` : text;
}

/**
 * Der Rueckfall, wenn kein Grundcode einen eigenen Satz hat.
 *
 * Er zeigt den Code NICHT an. Ein Mensch kann mit 'token_audience_mismatch'
 * nichts anfangen, und ihn trotzdem hinzuschreiben waere die bequeme Variante,
 * die NFR-UX-003 verbietet.
 *
 * DREI FAELLE STATT EINEM, und die Reihenfolge traegt:
 *
 *   0. Kam gar keine Antwort (Status 0), ist der Aufruf nicht zustande
 *      gekommen. Wiederholbar, und als einziger Fall mit einer Handlung, die
 *      die Nutzerin selbst ausfuehren kann. Der Satz ist ein LESESATZ: ob eine
 *      abgebrochene Schreibanfrage angekommen ist, weiss der Browser nicht.
 *      Schreibwege sagen das mit eigenem Satz ("Ob ... wurde, ist offen.",
 *      mit clientRequestId der Satz aus ADR-0060) und kommen hier nicht an.
 *   1. Eine Stoerung des Servers (5xx) ist wiederholbar -- gleich, ob sie eine
 *      lesbare Auskunft trug. Deshalb steht sie zuerst: ein 502 eines Gateways
 *      ist ohne lesbare Auskunft UND wiederholbar.
 *   2. Kam keine lesbare Auskunft an, hat an dieser Stelle nichts von LIAISON
 *      geantwortet. Wiederholen aendert daran nichts.
 *   3. Sonst hat LIAISON abgelehnt, nur ohne einen Grund, den dieser Bildschirm
 *      in Worte fassen kann. Eine Ablehnung ist eine Entscheidung; sie faellt
 *      beim zweiten Versuch genauso aus.
 *
 * AUSDRUECKLICH NICHT GEBAUT: eine eigene Meldung "die Anwendung ist noch nicht
 * bereitgestellt". Sie waere heute wahr und ab dem Tag, an dem der Dienst
 * steht, toter Text -- und sie verlangte eine Bedingung auf Status UND
 * Medientyp, die genau eine Ursache trifft. Fall 2 sagt dasselbe, ohne eine
 * Ursache zu behaupten, die der Bildschirm nicht kennt, und bleibt fuer jede
 * fremde Antwort richtig. Die Begruendung steht im Pull Request.
 */
export function fallbackMessage(facts: ResponseFacts): PlainMessage {
  const message = fallbackText(facts);
  // Nur wo der Satz auf die Nummer verweist, gehoert sie dazu (siehe withCorrelation).
  return facts.status > 0 && facts.hasCorrelationId && facts.correlationId !== undefined ? { ...message, correlationId: facts.correlationId } : message;
}

function fallbackText(facts: ResponseFacts): PlainMessage {
  if (facts.status <= 0) {
    // Gar keine Antwort: der Aufruf ist nicht zustande gekommen. Das ist der
    // einzige Fall, in dem die Nutzerin selbst etwas pruefen kann, und deshalb
    // nennt der naechste Schritt das zuerst.
    return {
      headline: 'LIAISON ist gerade nicht erreichbar.',
      // Nicht "hat LIAISON nicht erreicht": ob die Anfrage ankam, ist ohne Antwort offen.
      explanation: 'Es kam keine Antwort. Deine Anmeldung ist davon nicht betroffen.',
      nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
      retryable: true,
    };
  }
  if (facts.status >= 500) {
    return {
      headline: 'LIAISON ist gerade gestört.',
      explanation:
        'Deine Anmeldung ist in Ordnung. LIAISON konnte deine Anfrage im Moment nicht beantworten.',
      nextStep: withCorrelation(
        'Versuche es in einer Minute noch einmal. Bleibt es dabei, wende dich an die LIAISON-Administration.',
        facts.hasCorrelationId,
      ),
      retryable: true,
    };
  }
  if (!facts.answered) {
    return {
      // Ueberschrift und Erklaertext duerfen sich nicht widersprechen. Die
      // erste Fassung sagte oben "hat nicht geantwortet" und darunter "es kam
      // eine Antwort" -- fuer eine Nutzerin ohne Technikwissen ein
      // Widerspruch, den erst eine Zwischenueberlegung aufloest (Befund M1 des
      // Experience-Reviews, CM-UX-010). Jetzt sagt die Ueberschrift, was
      // LIAISON getan hat, und der Satz darunter, was stattdessen geschah.
      headline: 'LIAISON hat nicht geantwortet.',
      explanation:
        'Geantwortet hat etwas anderes als LIAISON — deshalb steht hier nichts über deinen Zugang. Deine Anmeldung ist davon nicht betroffen.',
      nextStep: `${withCorrelation(
        'Wende dich an die LIAISON-Administration.',
        facts.hasCorrelationId,
      )} Ein erneuter Versuch ändert daran nichts.`,
      retryable: false,
    };
  }
  return {
    headline: 'Das hat nicht funktioniert.',
    // Nicht "für den dieser Bildschirm noch keine Erklärung hat": das ist die
    // Sicht der Entwicklung auf ihr eigenes Werk und nicht die der Nutzerin
    // auf ihre Aufgabe (Befund m2 des Experience-Reviews).
    explanation: 'LIAISON hat deine Anfrage abgelehnt. Der Grund dafür lässt sich hier nicht erklären.',
    nextStep: withCorrelation(
      'Wende dich an die LIAISON-Administration.',
      facts.hasCorrelationId,
    ),
    retryable: false,
  };
}

/**
 * Die Uebersicht des eigenen Zugangs laesst sich nicht lesen -- und das ist
 * weder eine Ablehnung der Person noch eine Stoerung.
 *
 * WARUM EIN EIGENER SATZ STATT `no_matching_rule`: derselbe Grundcode
 * bedeutet an einer Fachroute "diese Ansicht darfst du nicht" und an
 * `/platform/access-decision` "die Selbstauskunft darfst du nicht lesen". Der
 * Satz der Fachrouten sagte auf dem Startbildschirm dreifach das Falsche
 * (Gegenprobe vom 14.09.2026): er klang wie eine Ablehnung des ganzen Zugangs,
 * er bat um eine Rolle, die er nicht nannte, und er liess die Nutzerin ohne
 * Handlung stehen. Bis die Selbstauskunft nach DEC-013 steht, ist dieser
 * Zustand der Alltag jeder Person ausserhalb der Diagnostics-Gruppe.
 *
 * Der Rollencode steht NICHT im Satz (NFR-UX-003); er liegt in der technischen
 * Sicht, wo die Administration ihn findet (NFR-UX-004).
 */
export function selfAccessUnreadableMessage(): PlainMessage {
  return {
    headline: 'Deine Zugangsübersicht lässt sich nicht anzeigen.',
    // Erst die Entwarnung, dann die Einschraenkung: der erste Satz sagt, was
    // fuer die Arbeit zaehlt. "Besondere Berechtigung" statt eines erfundenen
    // Eigennamens wie "Pruefrolle", den keine Administration in ihrem Katalog
    // faende (Experience-Review 14.09.2026, Befund H2).
    //
    // BENENNEND, NICHT BEHAUPTEND (ADR-0028 Nachtrag vom 14.09.2026, "Eine
    // Einschraenkung des Wortlauts"): "kannst arbeiten" war fuer eine Identitaet
    // ohne jede Fachrolle nicht zwingend wahr -- der Rahmen kennt in diesem
    // Zustand keine Rolle (ROLES_UNKNOWN), und der Server kann beim Oeffnen
    // eines Bereichs Nein sagen. Der Satz nennt deshalb, was der Rahmen WEISS
    // (angemeldet; die Uebersicht braucht eine Berechtigung) und WER entscheidet
    // (LIAISON beim Oeffnen), statt ein Ergebnis zu versprechen (ADR-0028
    // Ziffer 3: Angebot, kein Versprechen).
    explanation:
      'Du bist angemeldet. Nur diese Übersicht braucht eine besondere Berechtigung, die deinem Konto nicht zugewiesen ist. Was du in den Bereichen tun kannst, entscheidet LIAISON beim Öffnen.',
    // GENAU EINE Handlung. Die Bitte an die Administration steht bei dem
    // Rollencode, um den sie sich dreht -- unter "Technische Einzelheiten" --,
    // damit hier nicht zwei Schritte gleichrangig nebeneinander stehen
    // (Befund H1). Keine Vorgangsnummer im Satz: sie hilft niemandem, der
    // einfach weiterarbeitet.
    nextStep: 'Öffne einfach einen Bereich: „Kontakte“ unten oder einen anderen links.',
    retryable: false,
  };
}

export function plainMessageFor(reasonCode: string | undefined, facts: ResponseFacts): PlainMessage {
  return (reasonCode !== undefined ? MESSAGES[reasonCode] : undefined) ?? fallbackMessage(facts);
}

/** Nur für Tests und die Belegprüfung: welche Grundcodes einen Satz haben. */
export const TRANSLATED_REASON_CODES: readonly string[] = Object.freeze(Object.keys(MESSAGES));

/**
 * Anzahl Bereiche in Fachsprache. Eine Liste von UUIDs auf dem Bildschirm wäre
 * genau der Technikbegriff, den NFR-UX-003 ausschließt -- und für einen Menschen
 * ohne Aussage.
 */
export function areaSummary(count: number, bereiche?: readonly { readonly displayName: string; readonly kind?: HausArt | null }[]): string {
  if (count === 0) {
    return 'Du bist für keinen Bereich freigeschaltet.';
  }
  // Mit den Namen aus /party-search-options (A2, Entscheidung Dirigent 22.10.2026): "Verlag Nord
  // und Holding Süd". Passt die Liste nicht zur Zahl der Anmeldung, zaehlt der Satz nach Art.
  if (bereiche !== undefined && bereiche.length === count && bereiche.every((bereich) => bereich.displayName.trim() !== '')) {
    return `Du bist freigeschaltet für ${namenListe(bereiche.map((bereich) => bereich.displayName))}.`;
  }
  const arten = bereiche !== undefined && bereiche.length === count ? bereiche.map((bereich) => bereich.kind) : Array.from({ length: count }, () => null);
  return `Du bist für ${anzahlWort(arten)} freigeschaltet.`;
}
